import React, { useState, useMemo } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  useColorModeValue,
  useColorMode,
  Badge,
  Icon,
  Divider,
} from "@chakra-ui/react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiSun,
  FiMoon,
  FiLogOut,
} from "react-icons/fi";
import type { IconType } from "react-icons";
import { useNavigate, useLocation } from "react-router-dom";
import { CustomIcon } from "./CustomIcon";
import { useAuth } from "../context/AuthContext";
import {
  getSidebarItems,
  SIDEBAR_SECTIONS,
  ROUTE_PATHS,
} from "../config/routes";
import {
  getPermissionKey,
  getPermissionKeys,
  getPermissionResource,
} from "../utils/permissions";

import { Logo } from "./Logo";

const SIDEBAR_WIDTH = "280px";
const SIDEBAR_COLLAPSED_WIDTH = "80px";

interface NavItem {
  name: string;
  path: string;
  icon: IconType;
  badge?: number;
}

interface SystemAction {
  name: string;
  icon: IconType;
  onClick: () => void | Promise<void>;
  color: string;
}

export const SidebarContent = () => {
  const { colorMode, toggleColorMode } = useColorMode();

  const navigate = useNavigate();
  const location = useLocation();

  const [isOpen, onToggle] = useState(true);

  const containerBg = useColorModeValue("light.50", "dark.50");
  const menuBg = useColorModeValue("gray.50", "dark.500");
  const hoverBg = useColorModeValue("light.50", "dark.50");
  const activeBg = "brand.500";
  const menuItemContainerBg = useColorModeValue("light.700", "dark.700");
  const menuItemBorderColor = useColorModeValue("gray.200", "gray.700");
  const dividerLineColor = useColorModeValue("gray.300", "gray.600");
  const textColor = useColorModeValue("dark.900", "light.300");
  const activeIconColor = useColorModeValue("light.50", "light.50");
  const { logout, user, isSuperAdmin, resourceLabelMap } = useAuth();

  // Generate navigation sections based on user permissions
  const navSections = useMemo(() => {
    const userPermissions = getPermissionKeys(user?.role?.permissions);
    const userRoleValue = user?.role?.value;
    const sidebarGroups = getSidebarItems(
      require("../config/routes").routes,
      userPermissions,
      isSuperAdmin(),
      userRoleValue,
    );

    // Convert to NavSection format with proper ordering
    return Object.entries(SIDEBAR_SECTIONS)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key, section]) => ({
        title: section.title,
        items: (sidebarGroups[key] || []).map((route) => {
          const matchedPermission = user?.role?.permissions?.find(
            (permission) =>
              route.permissions?.includes(getPermissionKey(permission)),
          );
          const resource =
            getPermissionResource(matchedPermission) ||
            getPermissionResource(route.permissions?.[0]);
          const displayName =
            resource && resourceLabelMap[resource]
              ? resourceLabelMap[resource]
              : route.title;

          return {
            name: displayName,
            path: route.path,
            icon: route.icon!,
            badge: route.badge?.(),
          };
        }),
      }))
      .filter((section) => section.items.length > 0); // Only show sections with accessible items
  }, [user, isSuperAdmin, resourceLabelMap]);

  const systemActions: SystemAction[] = [
    {
      name: "بستن منو",
      icon: isOpen ? FiChevronRight : FiChevronLeft,
      onClick: () => onToggle((v) => !v),
      color: textColor,
    },
    {
      name: "تغییر تم",
      icon: colorMode === "light" ? FiMoon : FiSun,
      onClick: toggleColorMode,
      color: textColor,
    },
    {
      name: "خروج",
      icon: FiLogOut,
      onClick: async () => {
        await logout();
        navigate(ROUTE_PATHS.LOGIN);
      },
      color: "brand.500",
    },
  ];

  const systemSection = {
    title: "سیستم",
    isSystem: true,
    actions: systemActions,
  };

  return (
    <VStack
      w={isOpen ? SIDEBAR_WIDTH : SIDEBAR_COLLAPSED_WIDTH}
      h="100%"
      bg={containerBg}
      p={isOpen ? 5 : 3}
      display="flex"
      flexDirection="column"
      justifyContent="flex-start"
      transition="width 0.4s"
      borderRadius={"2xl"}
      flex={1}
      overflowY="auto"
    >
      <Box mb={isOpen ? 5 : 3}>
        <Logo
          variant="vertical"
          w={isOpen ? "100px" : "100%"}
          mx="auto"
          to="/"
        />
      </Box>

      {navSections.map((section, sectionIndex) => (
        <VStack key={section.title} w="100%" align="flex-start">
          {isOpen && (
            <Text
              fontSize="md"
              fontWeight="semibold"
              mb={1}
              textTransform="uppercase"
              dir="rtl"
            >
              {section.title}
            </Text>
          )}
          <VStack
            w="100%"
            bg={menuItemContainerBg}
            borderRadius={"xl"}
            overflow={"hidden"}
            spacing={0}
            border={"1px solid"}
            borderColor={menuItemBorderColor}
          >
            {section.items.map((item: NavItem, index: number) => {
              const isActive = location.pathname === item.path;
              const iconColor = isActive ? activeIconColor : textColor;
              const isLatestItem = section.items.length - 1 === index;

              return (
                <Box
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  cursor="pointer"
                  bg={isActive ? activeBg : menuBg}
                  _hover={{ bg: isActive ? "brand.400" : hoverBg }}
                  px={isOpen ? 4 : 2}
                  py={3}
                  display="flex"
                  alignItems="center"
                  borderBottom={isLatestItem ? "0px" : "1px solid"}
                  borderColor={menuItemBorderColor}
                  justifyContent={isOpen ? "space-between" : "center"}
                  transition="width 0.4s"
                  w="100%"
                >
                  <HStack spacing={3}>
                    <CustomIcon icon={item.icon} size={18} color={iconColor} />
                    {isOpen && (
                      <Text fontSize="sm" fontWeight="medium" color={iconColor}>
                        {item.name}
                      </Text>
                    )}
                  </HStack>
                  {isOpen &&
                    (isActive ? (
                      <Icon
                        verticalAlign="middle"
                        as={FiChevronLeft as any}
                        color={activeIconColor}
                        boxSize={4}
                      />
                    ) : item.badge ? (
                      <Badge
                        colorScheme="brand"
                        rounded="full"
                        px={2}
                        fontSize="xs"
                      >
                        {item.badge}
                      </Badge>
                    ) : null)}
                </Box>
              );
            })}
          </VStack>
          {isOpen && <Divider my={2} borderColor={dividerLineColor} />}
        </VStack>
      ))}

      {/* System Actions Section */}
      <VStack w="100%" align="flex-start">
        {isOpen && (
          <Text
            fontSize="md"
            fontWeight="semibold"
            mb={1}
            textTransform="uppercase"
            dir="rtl"
          >
            {systemSection.title}
          </Text>
        )}
        <VStack
          w="100%"
          bg={menuItemContainerBg}
          borderRadius={"xl"}
          overflow={"hidden"}
          spacing={0}
          border={"1px solid"}
          borderColor={menuItemBorderColor}
        >
          {systemSection.actions.map((action, index) => {
            const isLatestItem = systemSection.actions.length - 1 === index;

            return (
              <Box
                key={action.name}
                onClick={action.onClick}
                cursor="pointer"
                bg={menuBg}
                _hover={{ bg: hoverBg }}
                px={isOpen ? 4 : 2}
                py={3}
                display="flex"
                alignItems="center"
                borderBottom={isLatestItem ? "0px" : "1px solid"}
                borderColor={menuItemBorderColor}
                justifyContent={isOpen ? "space-between" : "center"}
                transition="width 0.4s"
                w="100%"
              >
                <HStack spacing={3}>
                  <CustomIcon
                    icon={action.icon}
                    size={18}
                    color={action.color}
                  />
                  {isOpen && (
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color={action.color}
                    >
                      {action.name}
                    </Text>
                  )}
                </HStack>
              </Box>
            );
          })}
        </VStack>
      </VStack>
    </VStack>
  );
};
